import { useEffect } from "react";
import { useRouter } from "../../store/router";
import { useBoothConfig } from "../../store/booth";
import { useI18n } from "../../i18n/useI18n";
import { useStylesForTheme, useSelectedTheme } from "../../hooks/useStyles";
import { Button } from "../../components/ui/Button";
import { BackButton } from "../../components/ui/BackButton";
import { StyleCard } from "./StyleCard";
import { nextRouteAfterStyle } from "../Consent/consentFlow";
import { useScrollHint } from "../../hooks/useScrollHint";

/**
 * STAGE · StyleSelect (HERIT 02, BOOTH-52) — 선택한 테마 하위 스타일을 3열 그리드(260×340 타일)로 놓고 하나를 고른다.
 * 6장까지는 스크롤 없이 한 화면, 그 이상이면 그리드만 세로 스크롤(제목·CTA 고정). 가로 iPad 는 4열, 폰은 2열.
 * 테마 없이 진입하면(새로고침 등) 테마 선택으로 되돌린다.
 */
export function StyleSelectView() {
  const go = useRouter((s) => s.go);
  const selectedId = useRouter((s) => s.selectedStyleId);
  const setStyle = useRouter((s) => s.setStyle);
  const selectedThemeId = useRouter((s) => s.selectedThemeId);
  const consentId = useRouter((s) => s.consentId);
  const consentDoc = useBoothConfig((s) => s.config?.consent ?? null);
  const { t, language } = useI18n();
  const theme = useSelectedTheme();
  const styles = useStylesForTheme(theme?.id ?? null);
  // 7장↑ 이면 그리드만 스크롤 — 넘침 여부를 data-scroll 로 달아 CSS 가 하단 페이드를 그린다
  const grid = useScrollHint<HTMLUListElement>(styles.length);

  // 테마가 선택돼 있지 않거나 노출 대상이 아니면 테마 선택으로.
  // route 조건: 처음으로(resetSession) 뒤 AnimatePresence 가 이 뷰를 퇴장 애니메이션 동안 붙잡아 두는데, 그때 theme 이 null 이 되어
  // 이 effect 가 attract 를 themeSelect 로 덮어쓰던 회귀(2026-10-21) — 아직 이 화면일 때만 되돌린다
  const route = useRouter((s) => s.route);
  useEffect(() => {
    if (route === "styleSelect" && (!selectedThemeId || !theme)) go("themeSelect");
  }, [route, selectedThemeId, theme, go]);

  // 진입 시 선택이 없거나 이 테마 밖의 스타일이면 첫 스타일을 기본 선택
  useEffect(() => {
    if (styles.length === 0) return;
    if (!selectedId || !styles.some((s) => s.id === selectedId)) {
      setStyle(styles[0]!.id);
    }
  }, [styles, selectedId, setStyle]);

  const selected = styles.find((s) => s.id === selectedId) ?? null;

  return (
    <div className="view view--style">
      {/* 제목 행 — 뒤로가기(원형 아이콘)는 제목의 세로 중앙·좌측에 절대배치 */}
      <div className="view__head">
        <BackButton label={t.styleSelect.back} onClick={() => go("themeSelect")} />
        <h1 className="view__title">{t.styleSelect.title}</h1>
      </div>

      <div className="view__body">
        <ul ref={grid.ref} data-scroll={grid.hint} className="style-grid no-scrollbar" role="listbox" aria-label={t.journey.style}>
          {styles.map((s) => (
            <StyleCard
              key={s.id}
              style={s}
              active={s.id === selected?.id}
              language={language}
              onSelect={() => setStyle(s.id)}
            />
          ))}
        </ul>
      </div>

      <div className="view__cta actions">
        <Button
          disabled={!selected}
          onClick={() => {
            if (selected) setStyle(selected.id);
            go(nextRouteAfterStyle(consentDoc, consentId));
          }}
        >
          {t.styleSelect.cta}
        </Button>
      </div>
    </div>
  );
}
